import React, { useState } from "react";
import axios from "axios";
import {
  ArrowRight, Users, ClipboardList, BarChart3, HeartHandshake, Check, School,
  Send, Loader2, CheckCircle2, Sparkles, Building2, LineChart,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { Reveal, SectionHeading, Eyebrow } from "../components/common";
import Seo from "../components/Seo";
import { schoolBenefits } from "../mock";

const iconMap = { Users, ClipboardList, BarChart3, HeartHandshake };

const rollout = [
  "Free onboarding session for your leadership and teachers",
  "Assessments run online during a single class period",
  "School level dashboards with focus, memory and creativity trends",
  "Individual cognitive profiles shared with every parent",
  "Ongoing CogniSol worksheets to strengthen mental skills",
];

export default function Schools() {
  const { toast } = useToast();
  const empty = { school: "", city: "", state: "", country: "", contact_person: "", email: "", phone: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["school", "city", "state", "country", "contact_person", "email", "phone"];
    if (required.some((k) => !form[k].trim())) {
      return toast({ title: "Please fill all fields", description: "Every field is required to arrange your subsidised dashboard." });
    }
    setLoading(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/school-enquiry`, form);
      setDone(true);
      toast({ title: "Enquiry sent", description: "Our schools team will contact you shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden">
      <Seo
        title="Cognitive Assessments for Schools & Institutions | MapMyKid"
        description="Bring MapMyKid to your school with a highly subsidised assessment dashboard. Give teachers cohort-wide cognitive insights, differentiate classroom activities and share meaningful cognitive profiles with every parent."
      />

      {/* HERO */}
      <section className="relative hero-gradient pt-32 sm:pt-40 pb-14">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center animate-fade-up">
          <Eyebrow>For Schools</Eyebrow>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.06] text-slate-900">
            The future of learning is <span className="brand-gradient-text">the mind.</span>
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            Forward looking schools no longer stop at marks. Give your institution cohort wide cognitive insight and become the school parents trust for clarity beyond exams. Apply below for a highly subsidised school dashboard.
          </p>
          <div className="mt-8 flex justify-center">
            <a href="#school-form"><Button className="cta-gradient text-white rounded-full h-14 px-7 text-base font-semibold">Apply for a school dashboard <ArrowRight className="ml-2 h-5 w-5" /></Button></a>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Why Schools Choose Us" title="An assessment your whole school can run." subtitle="Simple to roll out, rich in insight, and genuinely useful for teachers, leaders and parents alike." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {schoolBenefits.map((b, i) => {
              const Icon = iconMap[b.icon] || Users;
              return (
                <Reveal key={b.title} delay={i * 90}>
                  <div className="group flex gap-4 sm:gap-5 rounded-3xl border border-violet-100 bg-white p-6 sm:p-7 card-hover">
                    <span className="grid h-12 w-12 sm:h-14 sm:w-14 shrink-0 place-items-center rounded-2xl bg-violet-50 text-violet-600 transition-colors group-hover:cta-gradient group-hover:text-white"><Icon className="h-6 w-6 sm:h-7 sm:w-7" /></span>
                    <div><h3 className="text-lg sm:text-xl font-bold text-slate-900">{b.title}</h3><p className="mt-2 text-base text-slate-600 leading-relaxed">{b.desc}</p></div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROLLOUT */}
      <section className="section-pad soft-gradient">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <Reveal>
            <Eyebrow>Simple Rollout</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight">From onboarding to insights in one term.</h2>
            <ul className="mt-6 space-y-4">
              {rollout.map((t) => (
                <li key={t} className="flex items-start gap-3 text-base sm:text-lg text-slate-700"><Check className="mt-0.5 h-6 w-6 shrink-0 text-teal-500" /> {t}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={100}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Building2, k: "Whole School", v: "Single period rollout" },
                { icon: LineChart, k: "Dashboards", v: "Focus, memory, creativity" },
                { icon: Users, k: "Every Parent", v: "A meaningful profile" },
                { icon: Sparkles, k: "Subsidised", v: "Special school pricing" },
              ].map((c) => (
                <div key={c.k} className="rounded-3xl border border-violet-100 bg-white p-6 card-hover">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-violet-50 text-violet-600"><c.icon className="h-6 w-6" /></span>
                  <p className="mt-4 text-lg font-bold text-slate-900">{c.k}</p>
                  <p className="mt-1 text-base text-slate-500">{c.v}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Why It Matters" title="Move your school beyond marks, to the mind." />
          <div className="mt-8 space-y-5 text-base sm:text-lg leading-relaxed text-slate-600">
            <p>
              Every classroom holds a wide range of learners. Two students with the same marks can think in completely different ways: one may have strong memory but slower processing speed, another may be highly creative yet easily distracted. Traditional report cards cannot see these differences, which is why teachers often struggle to understand why a capable child underperforms. MapMyKid gives your school a clear, science backed picture of how each student actually thinks, learns and processes information.
            </p>
            <p>
              Our cognitive assessments measure intellect and memory, focus and concentration, decision making speed, creativity acumen, natural ability and multiple intelligence. When teachers can see these underlying strengths and gaps, they can plan differentiated activities, group students more thoughtfully, and support each learner in the way that suits them best. School leaders get dashboards that reveal cohort wide trends, helping guide teaching strategy for the whole institution.
            </p>
            <p>
              Rolling out MapMyKid is simple. Assessments run online during a single class period, with no special infrastructure required. Reports are generated instantly, and every parent receives a meaningful cognitive profile of their child, deepening their trust in your school. Combined with our CogniSol worksheets, schools can turn insight into action and help students steadily strengthen the mental skills that matter most for lifelong learning.
            </p>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="school-form" className="section-pad soft-gradient scroll-mt-20">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Apply Now" title="Get a highly subsidised school dashboard." subtitle="Share your school details and our team will set up a dedicated demo and special institutional pricing." />
          <Reveal className="mt-12">
            <div className="rounded-3xl border border-violet-100 bg-white p-6 sm:p-9 shadow-xl shadow-violet-100/50">
              {done ? (
                <div className="py-8 text-center">
                  <CheckCircle2 className="mx-auto h-16 w-16 text-teal-500" />
                  <h3 className="mt-4 text-2xl font-bold text-slate-900">Thank you!</h3>
                  <p className="mt-2 text-base text-slate-600">Your enquiry has been received. Our schools team will contact you shortly to arrange your subsidised dashboard.</p>
                  <Button onClick={() => { setDone(false); setForm(empty); }} variant="outline" className="mt-6 rounded-full h-12 px-6 text-base border-violet-200 text-violet-700 hover:bg-violet-50">Send another enquiry</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="flex items-center gap-2 text-violet-700"><School className="h-5 w-5" /><span className="text-base font-semibold">School details</span></div>
                  <div className="space-y-2">
                    <Label className="text-base">School name *</Label>
                    <Input value={form.school} onChange={update("school")} placeholder="Your school name" className="h-12 text-base rounded-xl" data-testid="school-name-input" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-base">City *</Label>
                      <Input value={form.city} onChange={update("city")} placeholder="City" className="h-12 text-base rounded-xl" data-testid="school-city-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-base">State *</Label>
                      <Input value={form.state} onChange={update("state")} placeholder="State" className="h-12 text-base rounded-xl" data-testid="school-state-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-base">Country *</Label>
                      <Input value={form.country} onChange={update("country")} placeholder="Country" className="h-12 text-base rounded-xl" data-testid="school-country-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-base">Contact person *</Label>
                      <Input value={form.contact_person} onChange={update("contact_person")} placeholder="Full name" className="h-12 text-base rounded-xl" data-testid="school-contact-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-base">Email *</Label>
                      <Input type="email" value={form.email} onChange={update("email")} placeholder="you@school.edu" className="h-12 text-base rounded-xl" data-testid="school-email-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-base">Phone *</Label>
                      <Input value={form.phone} onChange={update("phone")} placeholder="Phone number" className="h-12 text-base rounded-xl" data-testid="school-phone-input" />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full cta-gradient text-white rounded-full h-14 text-[17px] font-semibold" data-testid="school-submit-button">
                    {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending...</> : <>Submit Enquiry <Send className="ml-2 h-5 w-5" /></>}
                  </Button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
